import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { adminApi, type Track, type Pathway } from '@/api';

const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];

const ICON_OPTIONS = ['BrainCircuit', 'Zap', 'Cpu', 'Scale', 'Users', 'Palette', 'Briefcase', 'BarChart3', 'Megaphone', 'ShoppingBag', 'ShieldAlert'];

export function TrackForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pathways, setPathways] = useState<Pathway[]>([]);
  const [form, setForm] = useState<Partial<Track> & { id: string; name: string; description: string; icon: string; pathwayId: string; difficulty: string; color: string }>({
    id: '',
    name: '',
    description: '',
    icon: 'Zap',
    pathwayId: '',
    difficulty: 'Beginner',
    color: 'indigo',
  });

  useEffect(() => {
    adminApi.pathways.list().then(setPathways).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew && pathways.length && !form.pathwayId) {
      setForm((f) => ({ ...f, pathwayId: pathways[0]!.id }));
    }
  }, [isNew, pathways, form.pathwayId]);

  useEffect(() => {
    if (isNew) return;
    adminApi.tracks.get(id!).then((t) => {
      setForm({
        id: t.id,
        name: t.name,
        description: t.description,
        icon: t.icon,
        pathwayId: t.pathway.id,
        difficulty: t.difficulty,
        color: t.color,
      });
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isNew && !form.pathwayId) {
      setError('Please select a pathway');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.tracks.create(form as Parameters<typeof adminApi.tracks.create>[0]);
        navigate('/tracks');
      } else {
        await adminApi.tracks.update(id!, {
          name: form.name,
          description: form.description,
          icon: form.icon,
          pathwayId: form.pathwayId,
          difficulty: form.difficulty,
          color: form.color,
        });
        navigate('/tracks');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Link to="/tracks" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Tracks
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New track' : 'Edit track'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ID</label>
          <input
            value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            required
            disabled={!isNew}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
            placeholder="e.g. ai-basics"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Icon (ICON_MAP key)</label>
          <select
            value={form.icon}
            onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          >
            {ICON_OPTIONS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Pathway</label>
            <select
              value={form.pathwayId}
              onChange={(e) => setForm((f) => ({ ...f, pathwayId: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            >
              <option value="">Select pathway</option>
              {pathways.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty</label>
            <select
              value={form.difficulty}
              onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
          <input
            value={form.color}
            onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="e.g. indigo"
          />
        </div>
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/tracks" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
